import {ENTITY_SERVICE_REGISTRY, EntityServiceEntry} from '../../core/shared/dto/dto-service/entity-service-registry';
import {MvsCrudModeEnum} from '../../core/shared/service/crud/mvs-crud-mode.enum';
import {ChConversationDataService} from './service/domain/ch-conversation/ch-conversation-data.service';
import {ChInboundService} from './service/domain/ch-inbound/ch-inbound.service';
import {ChInboundSourceConfigObjectComponent} from './component/object-components/ch-inbound-source-config-object-component/ch-inbound-source-config-object.component';

/**
 * Entity registry for the Communication Hub (ch) module.
 *
 * Registration decisions per MvsCrudService skill SC6/SC7 rules:
 *
 * ROUTE-ONLY (no service file) — entities accessed exclusively via QL or
 * whose full CRUD operations require no custom component dialogs in Phase 2:
 *   - ch.ChCustomerInteraction    — read-only projection, QL-only (Phase 1)
 *   - ch.ChConversationSession    — sub-entity of ChConversation; no standalone dialog
 *   - ch.ChIdentityMergeLog       — append-only audit log; no create/edit dialog
 *   - ch.ChChannelCompatibility   — config entity; standard CRUD grid on config page
 *
 * SERVICE-BACKED:
 *   - ch.ChConversation — wired to ChConversationDataService which extends
 *     MvsCrudService and adds QL reads + three action endpoints (replyViaTicket,
 *     markDone, reactivate). The service also provides the polling helper.
 *
 * ROUTE-ONLY (no service file):
 *   - ch.ChChannelIdentity  — accessed via QL in ChConversationDataService
 *   - ch.ChMessage          — accessed via QL; GoBD-immutable after creation
 *
 * Phase 3 (TM-1000001922) — inbound channels. All 5 are route-only:
 *   - ch.ChInboundMessage       — read-only system work-queue; the review
 *                                 workbench (AP-3.8) is its FE surface, NOT a
 *                                 config page. SERVICE-BACKED by ChInboundService
 *                                 (extends MvsCrudService) which adds the QL
 *                                 review/error reads + the workbench action
 *                                 endpoints (assign-customer / create-lead /
 *                                 statistics) + the 30 s polling helper.
 *   - ch.ChInboundSourceConfig  — COMPLEX config entity (3 same-module
 *                                 configurable children: ChWhatsappNumberConfig,
 *                                 ChMailboxConfig, ChSmsConfig). Per the Complex
 *                                 Configuration Entity Rule (config-page-audit
 *                                 CONFIG-003/010) it is registered with an
 *                                 objectComponent (ChInboundSourceConfigObject-
 *                                 Component) for MvsCrudModeEnum.update so a
 *                                 config-page row-click opens its Object Page
 *                                 (left-nav over the three children), NOT a flat
 *                                 sidebar form.
 *   - ch.ChWhatsappNumberConfig — child config entity; CRUD via the parent's
 *                                 Object Page left-nav widget (filtered by FK).
 *   - ch.ChMailboxConfig        — child config entity; CRUD via the parent's
 *                                 Object Page left-nav widget (filtered by FK).
 *   - ch.ChSmsConfig            — child config entity; CRUD via the parent's
 *                                 Object Page left-nav widget (filtered by FK).
 * The three child entities are route-only: they have no standalone config-page
 * nav item; their grids live exclusively inside the parent ObjectComponent.
 *
 * AP-4.10 (TM-1000001923) — WhatsApp template registry. Route-only:
 *   - ch.ChWhatsappTemplateConfig — leaf config entity (NO same-module
 *                                   configurable children). Its FK to
 *                                   ch.ChWhatsappNumberConfig is OPTIONAL/
 *                                   nullable, so per the Complex Configuration
 *                                   Entity Rule (config-page-audit CONFIG-003)
 *                                   it is 'Simple' and rendered as a FLAT
 *                                   top-level config-page nav item
 *                                   ("WhatsApp-Vorlagen") — a standard CRUD grid,
 *                                   NOT nested inside the number config. No
 *                                   custom dialog/objectComponent is required.
 *
 * R4 (TM-1000001925) — canned responses / Textbausteine. Route-only:
 *   - ch.ChCannedResponse — leaf config entity (NO same-module configurable
 *                           children, NO FK). Per the Complex Configuration
 *                           Entity Rule (config-page-audit CONFIG-003) it is
 *                           'Simple' and rendered as a FLAT top-level config-page
 *                           nav item ("Textbausteine") — a standard CRUD grid. It
 *                           is ALSO read (active only) by the conversation
 *                           composer's "Textbaustein einfügen" picker via
 *                           GET /mvsa/ch/chCannedResponses. No custom dialog/
 *                           objectComponent is required.
 *
 * @ticket TM-1000001919
 * @ticket TM-1000001921
 * @ticket TM-1000001922
 * @ticket TM-1000001923
 * @ticket TM-1000001925
 */
export const CH_ENTITY_PROVIDER = [
    {
        provide: ENTITY_SERVICE_REGISTRY,
        useValue: [
            // Phase 1 — read-only projection, QL-only
            ['ch.ChCustomerInteraction', {route: '/ch/chCustomerInteractions'}],

            // Phase 2 — 6 new ch entities
            ['ch.ChChannelIdentity',      {route: '/ch/chChannelIdentitys'}],
            ['ch.ChConversation',         {route: '/ch/chConversations', service: ChConversationDataService}],
            ['ch.ChConversationSession',  {route: '/ch/chConversationSessions'}],
            ['ch.ChMessage',              {route: '/ch/chMessages'}],
            ['ch.ChChannelCompatibility', {route: '/ch/chChannelCompatibilitys'}],
            ['ch.ChIdentityMergeLog',     {route: '/ch/chIdentityMergeLogs'}],

            // Phase 3 — inbound channels (work-queue + 4 config entities)
            ['ch.ChInboundMessage',       {route: '/ch/chInboundMessages', service: ChInboundService}],
            ['ch.ChInboundSourceConfig',  {
                route: '/ch/chInboundSourceConfigs',
                objectComponent: [
                    {mode: MvsCrudModeEnum.update, component: ChInboundSourceConfigObjectComponent},
                ],
            }],
            ['ch.ChWhatsappNumberConfig', {route: '/ch/chWhatsappNumberConfigs'}],
            ['ch.ChMailboxConfig',        {route: '/ch/chMailboxConfigs'}],
            ['ch.ChSmsConfig',            {route: '/ch/chSmsConfigs'}],

            // AP-4.10 — WhatsApp template registry (flat config-page nav item)
            ['ch.ChWhatsappTemplateConfig', {route: '/ch/chWhatsappTemplateConfigs'}],

            // R4 — canned responses / Textbausteine (flat config-page nav item +
            // composer picker). GET/POST /mvsa/ch/chCannedResponses.
            ['ch.ChCannedResponse', {route: '/ch/chCannedResponses'}],
        ] as EntityServiceEntry[],
        multi: true
    }
];
